/**
 * @file     useVisitorProfile.ts
 * @purpose  React hook wrapping the visitor profile localStorage service for reactive client-side access. Reads synchronously on first render, no async init bugs.
 */
'use client';

import { useState, useCallback, useEffect, useLayoutEffect } from 'react';
import {
  readProfile,
  writeDataPoint as serviceWriteDataPoint,
  type VisitorProfile,
  type DataPointEntry,
  type DataPointValue,
} from '@/services/visitor-profile';

// Custom event name used to notify all hook instances when profile changes
const PROFILE_CHANGE_EVENT = 'visitor-profile-change';

function dispatchProfileChange() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PROFILE_CHANGE_EVENT));
  }
}

export function useVisitorProfile() {
  // Hydration-safe init: null on server and first client render (matches SSR).
  // useLayoutEffect fires synchronously after DOM mutation but BEFORE paint,
  // so the real profile is set before the user sees anything.
  const [profile, setProfile] = useState<VisitorProfile | null>(null);

  // SSR-safe: useLayoutEffect warns on server, so use useEffect as fallback
  const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

  useIsomorphicLayoutEffect(() => {
    setProfile(readProfile());
  }, []);

  // Listen for changes from other hook instances
  useEffect(() => {
    const handler = () => setProfile(readProfile());
    window.addEventListener(PROFILE_CHANGE_EVENT, handler);
    return () => window.removeEventListener(PROFILE_CHANGE_EVENT, handler);
  }, []);

  const writeDataPoint = useCallback(
    (key: string, value: DataPointValue, source: string) => {
      serviceWriteDataPoint(key, value, source);
      setProfile(readProfile());
      dispatchProfileChange();
    },
    [],
  );

  const getDataPoint = useCallback(
    (key: string): DataPointEntry | null => {
      return profile?.dataPoints[key] ?? null;
    },
    [profile],
  );

  const hasDataPoint = useCallback(
    (key: string): boolean => {
      return key in (profile?.dataPoints ?? {});
    },
    [profile],
  );

  const isReturningVisitor =
    profile !== null && Object.keys(profile.dataPoints).length > 0;

  return { profile, getDataPoint, hasDataPoint, writeDataPoint, isReturningVisitor };
}
